// MS388 (Sprint 467, 2026-05-20): frontend mirror of the canonical
// notification_prefs read helper that ships with edge functions at
// supabase/functions/_shared/notification-prefs.ts.
//
// Why a separate file:
//   - The edge-function helper imports `npm:`-prefixed types and lives
//     in the Deno-runtime tree; we can't reuse it from the Vite browser
//     bundle without dragging in the edge runtime shims.
//   - The shape, defaults, and parsing logic MUST stay identical so a
//     buyer toggling a switch in Settings reads back the same value
//     the cron read the night before. The canonical default is the
//     migration's `DEFAULT '...'::jsonb` clause in
//     20260519130000_ms383_profile_notification_prefs.sql.
//
// Used by: Settings → Notification cadence section (live toggles).
// The chat agent's update_notification_pref / update_cadence tools
// remain the alternative write path.

export type EmailPrefKey =
  | "celebration_reminders"
  | "order_updates"
  | "weekly_digest";

export type DayOfWeek =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday";

export interface NotificationPrefs {
  emails: Record<EmailPrefKey, boolean>;
  cadence: {
    celebration_lead_days: number;
    digest_day_of_week: DayOfWeek;
  };
}

/**
 * Canonical defaults. Mirror the migration's DEFAULT clause exactly so
 * a profile row that's never been touched and a profile row whose
 * notification_prefs is corrupt both fall back to identical behaviour.
 */
export const DEFAULT_NOTIFICATION_PREFS: NotificationPrefs = {
  emails: {
    celebration_reminders: true,
    order_updates: true,
    weekly_digest: false,
  },
  cadence: {
    celebration_lead_days: 1,
    digest_day_of_week: "monday",
  },
};

const VALID_DAYS: ReadonlySet<DayOfWeek> = new Set<DayOfWeek>([
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
]);

function isValidDay(value: unknown): value is DayOfWeek {
  return typeof value === "string" && VALID_DAYS.has(value as DayOfWeek);
}

/**
 * Parse a notification_prefs value (the raw jsonb returned from the
 * profiles row) into the structured shape. Missing or invalid fields
 * fall back to defaults, so the caller never has to null-check.
 */
export function parseNotificationPrefs(raw: unknown): NotificationPrefs {
  if (!raw || typeof raw !== "object") return DEFAULT_NOTIFICATION_PREFS;
  const rec = raw as Record<string, unknown>;

  const emailsRaw =
    rec.emails && typeof rec.emails === "object"
      ? (rec.emails as Record<string, unknown>)
      : {};
  const cadenceRaw =
    rec.cadence && typeof rec.cadence === "object"
      ? (rec.cadence as Record<string, unknown>)
      : {};

  const leadDaysRaw = cadenceRaw.celebration_lead_days;
  const leadDays =
    typeof leadDaysRaw === "number" &&
    Number.isInteger(leadDaysRaw) &&
    leadDaysRaw >= 0 &&
    leadDaysRaw <= 30
      ? leadDaysRaw
      : DEFAULT_NOTIFICATION_PREFS.cadence.celebration_lead_days;

  const digestDay = isValidDay(cadenceRaw.digest_day_of_week)
    ? cadenceRaw.digest_day_of_week
    : DEFAULT_NOTIFICATION_PREFS.cadence.digest_day_of_week;

  return {
    emails: {
      celebration_reminders:
        typeof emailsRaw.celebration_reminders === "boolean"
          ? emailsRaw.celebration_reminders
          : DEFAULT_NOTIFICATION_PREFS.emails.celebration_reminders,
      order_updates:
        typeof emailsRaw.order_updates === "boolean"
          ? emailsRaw.order_updates
          : DEFAULT_NOTIFICATION_PREFS.emails.order_updates,
      weekly_digest:
        typeof emailsRaw.weekly_digest === "boolean"
          ? emailsRaw.weekly_digest
          : DEFAULT_NOTIFICATION_PREFS.emails.weekly_digest,
    },
    cadence: {
      celebration_lead_days: leadDays,
      digest_day_of_week: digestDay,
    },
  };
}

export const DAY_OF_WEEK_OPTIONS: ReadonlyArray<{
  value: DayOfWeek;
  label: string;
}> = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
  { value: "sunday", label: "Sunday" },
];

/**
 * Lead-day options for the "remind me N days before" select. We cover
 * 0–14 explicitly (the realistic window) and the migration permits
 * up to 30. A number input would also work but the discrete list
 * matches the Switch + Select pattern used elsewhere in this page
 * and lines up with how the chat agent suggests common values.
 */
export const CELEBRATION_LEAD_DAY_OPTIONS: ReadonlyArray<{
  value: number;
  label: string;
}> = [
  { value: 0, label: "Day of" },
  { value: 1, label: "1 day before" },
  { value: 2, label: "2 days before" },
  { value: 3, label: "3 days before" },
  { value: 5, label: "5 days before" },
  { value: 7, label: "7 days before" },
  { value: 14, label: "14 days before" },
];
